import { colorPalette } from "@iewc/Utilities/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    heading = "heading",
    description = "description",
    statusSequence = "statusSequence",
    allProductsInLastStepLabel = "allProductsInLastStepLabel",
}

export interface StatusSequenceModel {
    fields: {
        labelSingular: string;
        labelPlural: string;
    };
}
interface OwnProps {
    tracker: any[];
    fields: {
        [fields.heading]: string;
        [fields.description]: string;
        [fields.statusSequence]: StatusSequenceModel[];
        [fields.allProductsInLastStepLabel]: string;
    };
}

type Props = OwnProps;

interface OrderStatusOrderTrackerStyles {
    list?: InjectableCss;
    item?: InjectableCss;
    qty?: InjectableCss;
    label?: InjectableCss;
    wrapper?: InjectableCss;
    prev?: InjectableCss;
    next?: InjectableCss;
}

const styles: OrderStatusOrderTrackerStyles = {
    list: {
        css: css`
            display: flex;
            justify-content: space-between;
            margin: 20px 0;
            gap: 14px;

            * {
                margin-top: 0;
            }

            @media screen and (max-width: 1023px) {
                background: #fff;
                flex-wrap: wrap;
                margin: 0 0 15px;
            }
        `,
    },
    item: {
        css: css`
            flex-direction: column;
            padding-right: 35px;
            height: 122px;
            flex-grow: 1;
            width: 100%;
            position: relative;
            display: inline-flex;
            align-items: center;

            @media screen and (max-width: 1023px) {
                width: 100%;
                z-index: 0;
                padding-right: 0;
                height: auto;
                margin-top: -1px;

                &:nth-child(1) {
                    z-index: 9;
                }

                &:nth-child(2) {
                    z-index: 8;
                }

                &:nth-child(3) {
                    z-index: 7;
                }

                &:nth-child(4) {
                    z-index: 6;
                }

                &:nth-child(5) {
                    z-index: 5;
                }

                &:nth-child(6) {
                    z-index: 4;
                }

                &:nth-child(7) {
                    z-index: 3;
                }
            }
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 14px;
            background: #fff;
            flex-direction: column;
            border: 1px solid #ddd;
            padding: 22px 0 15px 14px;
            border-width: 1px;
            position: relative;
            width: 100%;
            z-index: 2;

            @media screen and (max-width: 1023px) {
                flex-direction: row;
                align-items: center;
                justify-content: flex-start;
                padding: 15px;
            }
        `,
    },
    qty: {
        css: css`
            border-radius: 50%;
            display: inline-flex;
            width: 47px;
            height: 47px;
            justify-content: center;
            align-items: center;
            background: #006494;
            color: ${colorPalette.common?.background};
            font-size: 24px;
            font-weight: 600;

            &.inactive {
                background: #ddd;
                color: #4a4a4a;
            }

            li:last-child &.active {
                background: #019d05;
            }
        `,
    },
    label: {
        css: css`
            font-size: 18px;
            line-height: 22px;
            font-weight: 600;
        `,
    },
    prev: {
        css: css`
            position: absolute;
            top: 0;
            left: -35px;
            z-index: 3;

            @media screen and (max-width: 1023px) {
                display: none;
            }

            &::before {
                content: "";
                position: absolute;
                top: 0;
                left: 0;
                bottom: 0;
                border-left: 35px solid transparent;
                border-bottom: 61px solid #ddd;
                border-top: 61px solid #ddd;

                @media screen and (max-width: 1023px) {
                    content: "";
                    position: absolute;
                    z-index: 1;
                    background-color: #ddd;
                    height: 1px;
                    left: 0;
                    right: calc(50% - 30px);
                }
            }

            &::after {
                content: "";
                position: absolute;
                top: 1px;
                left: 2px;
                bottom: 1px;
                border-left: 35px solid transparent;
                border-bottom: 60px solid #fff;
                border-top: 60px solid #fff;

                @media screen and (max-width: 1023px) {
                    content: "";
                    position: absolute;
                    z-index: 1;
                    background-color: #ddd;
                    height: 1px;
                    left: calc(50% - 30px);
                    right: 0;
                }
            }
        `,
    },
    next: {
        css: css`
            position: absolute;
            right: 0;
            top: 0;
            z-index: 3;

            @media screen and (max-width: 1023px) {
                bottom: -11px;
                left: 0;
                right: 0;
                top: auto;
                z-index: 10;
            }

            &::before {
                content: "";
                position: absolute;
                z-index: 1;
                top: 0;
                bottom: 0;
                right: 1px;
                border-left: 35px solid #ddd;
                border-top: 61px solid transparent;
                border-bottom: 61px solid transparent;

                @media screen and (max-width: 1023px) {
                    content: "";
                    position: absolute;
                    z-index: 1;
                    border-top: 11px solid #ddd;
                    left: calc(50% - 30px);
                    right: calc(50% - 30px);
                    bottom: 0;
                    border-left: 30px solid transparent;
                    border-right: 30px solid transparent;
                    border-bottom: 0;
                    top: auto;
                }
            }

            &::after {
                content: "";
                position: absolute;
                z-index: 2;
                top: 0;
                bottom: 0;
                right: 2px;
                border-left: 35px solid #fff;
                border-top: 61px solid transparent;
                border-bottom: 61px solid transparent;

                @media screen and (max-width: 1023px) {
                    content: "";
                    position: absolute;
                    z-index: 2;
                    border-top: 11px solid #fff;
                    left: calc(50% - 30px);
                    right: calc(50% - 30px);
                    bottom: 1px;
                    border-left: 30px solid transparent;
                    border-right: 30px solid transparent;
                    border-bottom: 0;
                    top: auto;
                }
            }
        `,
    },
};

const CHECK_ICON = (
    <svg xmlns="http://www.w3.org/2000/svg" xmlSpace="preserve" width="20" height="20" viewBox="0 0 17.837 17.837">
        <path
            fill="currentColor"
            d="M16.145 2.571a.7.7 0 0 0-.99 0L6.92 10.804l-4.241-4.27a.698.698 0 0 0-.989 0L.204 8.019a.703.703 0 0 0 0 .99l6.217 6.258a.704.704 0 0 0 .99 0L17.63 5.047a.7.7 0 0 0 0-.994l-1.485-1.482z"
        />
    </svg>
);

const OrderTracker: FC<Props> = ({ tracker, fields }) => {
    if (!tracker?.length) {
        return null;
    }

    const compileStatusLabel = (status: any, index: any) => {
        let label = `${status.qty} ${fields.statusSequence?.[index]?.fields?.labelPlural}`;

        if (status.qty === 1) {
            label = `${status.qty} ${fields.statusSequence?.[index]?.fields?.labelSingular}`;
        } else if (status.qty === -1) {
            label = fields.allProductsInLastStepLabel;
        }

        return label;
    };

    return (
        <StyledWrapper>
            {fields.heading && (
                <Typography as="h3" variant="h4">
                    {fields.heading}
                </Typography>
            )}
            {fields.description && <Typography as="div">{parse(fields.description, parserOptions)}</Typography>}
            <StyledWrapper as="ul" {...styles.list}>
                {tracker.map((status: any, index: any) => (
                    <StyledWrapper as="li" key={index} {...styles.item}>
                        {index > 0 && <StyledWrapper as="div" {...styles.prev} />}
                        <StyledWrapper as="div" {...styles.wrapper}>
                            <StyledWrapper as="p" {...styles.qty} className={status.qty !== 0 ? "active" : "inactive"}>
                                {status.qty === -1 ? CHECK_ICON : status.qty}
                            </StyledWrapper>
                            <StyledWrapper as="p" {...styles.label}>
                                {compileStatusLabel(status, index)}
                            </StyledWrapper>
                        </StyledWrapper>
                        {index + 1 < tracker.length && <StyledWrapper as="div" {...styles.next} />}
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default OrderTracker;
